import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import DownloadIcon from "@mui/icons-material/Download";
import { styled } from "@mui/material/styles";
import { appThemeColors } from "styles/appTheme";
import { appThemeFonts } from "styles/appThemeFonts";
import { appThemeRadius } from "styles/appThemeTokens";

const uiFont = appThemeFonts.family.ui;

const ExportToolbarButton = styled(Button)(() => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  height: 34,
  minHeight: 34,
  padding: "0 14px",
  backgroundColor: appThemeColors.background.paper,
  border: `1px solid ${appThemeColors.border.strong}`,
  borderRadius: appThemeRadius.pill,
  color: appThemeColors.text.primary,
  fontSize: "12.5px",
  fontWeight: 500,
  fontFamily: uiFont,
  textTransform: "none",
  whiteSpace: "nowrap",
  boxShadow: "none",
  lineHeight: 1.2,
  "& .MuiButton-startIcon": {
    marginRight: 0,
    marginLeft: 0,
    color: appThemeColors.text.lightBlue,
    "& > *:nth-of-type(1)": {
      fontSize: 14,
    },
  },
  "&:hover": {
    backgroundColor: appThemeColors.background.kbd,
    borderColor: appThemeColors.text.lightBlue,
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: appThemeColors.background.paper,
    borderColor: appThemeColors.border.default,
    color: appThemeColors.text.faintLight,
    opacity: 0.55,
  },
}));

export default function TableExportCsv({
  visible,
  onSimpleExport,
  disabled,
  disabledTitle,
  loading,
}) {
  if (!visible) {
    return null;
  }

  const button = (
    <ExportToolbarButton
      variant="outlined"
      size="small"
      startIcon={
        loading ? (
          <CircularProgress size={14} color="inherit" aria-hidden />
        ) : (
          <DownloadIcon aria-hidden />
        )
      }
      onClick={onSimpleExport}
      disabled={disabled || loading}
      data-testid="export-csv-simple"
      aria-busy={loading || undefined}
    >
      {loading ? "Exporting…" : "Export CSV"}
    </ExportToolbarButton>
  );

  if (
    !loading &&
    disabled &&
    typeof disabledTitle === "string" &&
    disabledTitle.trim()
  ) {
    return (
      <Tooltip title={disabledTitle} placement="top" arrow enterDelay={200}>
        <Box component="span" sx={{ display: "inline-block" }}>
          {button}
        </Box>
      </Tooltip>
    );
  }

  return button;
}

TableExportCsv.propTypes = {
  visible: PropTypes.bool,
  onSimpleExport: PropTypes.func,
  disabled: PropTypes.bool,
  disabledTitle: PropTypes.string,
  loading: PropTypes.bool,
};

TableExportCsv.defaultProps = {
  visible: true,
  onSimpleExport: undefined,
  disabled: false,
  disabledTitle: "",
  loading: false,
};
